import { AxiosResponse } from "axios";
import { Requests } from "config/api";
import { getRequest } from "api/commonActions/get";
import { postRequest } from "api/commonActions/post";
import { patchRequest } from "api/commonActions/update";
import {
  BroadcastNotificationProps,
  BroadcastNotificationReturnType,
  GetNotificationsProps,
  GetNotificationsReturnType,
  GetNotificationsUnreadCountReturnType,
  MarkAllNotificationsReadReturnType,
  MarkNotificationReadProps,
  MarkNotificationReadReturnType,
} from "declarations/notificationServices";

export const GetNotifications = async ({
  setLoading,
  limit,
  offset,
  unreadOnly,
  type,
}: GetNotificationsProps): Promise<GetNotificationsReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getNotifications,
      queryParams: {
        limit,
        offset,
        unreadOnly,
        type,
      },
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      notifications: response?.data?.notifications || [],
      count: response?.data?.count,
      unreadCount: Number(response?.data?.unreadCount || 0),
      hasMore: Boolean(response?.data?.hasMore),
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetNotificationsUnreadCount = async ({
  setLoading,
}: Pick<GetNotificationsProps, "setLoading">): Promise<GetNotificationsUnreadCountReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getNotificationsUnreadCount,
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      unreadCount: Number(response?.data?.unreadCount || 0),
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const MarkNotificationRead = async ({
  id,
  setLoading,
}: MarkNotificationReadProps): Promise<MarkNotificationReadReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.markNotificationRead,
      pathVariables: { id },
      payload: {},
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      notification: response?.data?.notification || null,
      unreadCount: Number(response?.data?.unreadCount || 0),
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const MarkAllNotificationsRead = async ({
  setLoading,
}: Pick<GetNotificationsProps, "setLoading">): Promise<MarkAllNotificationsReadReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.markAllNotificationsRead,
      payload: {},
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      unreadCount: Number(response?.data?.unreadCount || 0),
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const BroadcastNotification = async ({
  setLoading,
  recipientScope = "all",
  recipients,
  title,
  body,
  type = "system",
  titleAmharic,
  bodyAmharic,
  carId,
}: BroadcastNotificationProps): Promise<BroadcastNotificationReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.broadcastNotification,
      payload: {
        recipientScope,
        recipients,
        title,
        body,
        type,
        carId,
        data:
          titleAmharic || bodyAmharic
            ? { titleAmharic, bodyAmharic }
            : undefined,
      },
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      recipientCount: Number(response?.data?.recipientCount || 0),
      notificationCount: Number(response?.data?.notificationCount || 0),
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};
